import { useEffect, useRef } from "react";

import {
  FRONTEND_STORAGE_KEYS,
  useGetStorageValueQuery,
  useSetStorageValueMutation,
} from "@js/apps/common/frontend-storage";
import { useUser } from "@js/apps/common/hooks/use-user";
import { useGetWithdrawalMethodsQuery } from "@js/apps/withdrawal/api/api";
import { useAppDispatch } from "@js/hooks/redux";

import { useCurrentFreelancerProfileCompletion } from "../../hooks/use-current-freelancer-profile-completion";

import {
  FreelancerBillingAnnouncementModal,
  openFreelancerBillingAnnoucementModal,
} from "./freelancer-billing-announcement-modal";

const CUTOFF_DATE = new Date("2026-07-21T00:00:00.000Z");

export const FreelancerBillingAnnouncementProvider = () => {
  const user = useUser();
  const { isLoading: isLoadingProfileCompletion, isProfileCompleted } =
    useCurrentFreelancerProfileCompletion();
  const isUserCreatedBeforeCutoff =
    !!user?.created && new Date(user.created) < CUTOFF_DATE;

  const checkIfShouldShowAnnoucement =
    isUserCreatedBeforeCutoff &&
    !!user?.is_verified &&
    !isLoadingProfileCompletion;
  const { data: announcementShown, isLoading } = useGetStorageValueQuery(
    { key: FRONTEND_STORAGE_KEYS.freelancer_billing_announcement_shown },
    { skip: !checkIfShouldShowAnnoucement },
  );
  const { data: withdrawalMethods, isLoading: isFetchingWithdrawalMethods } =
    useGetWithdrawalMethodsQuery(undefined, {
      skip: !checkIfShouldShowAnnoucement,
    });
  const [setStorageValue] = useSetStorageValueMutation();
  const dispatch = useAppDispatch();

  const shouldShowAnnouncement =
    checkIfShouldShowAnnoucement && !isLoading && !announcementShown;

  const wasShown = useRef(false);

  useEffect(() => {
    if (
      !shouldShowAnnouncement ||
      wasShown.current ||
      isFetchingWithdrawalMethods ||
      !withdrawalMethods
    ) {
      return;
    }
    wasShown.current = true;
    const dismissAnnoucement = () => {
      void setStorageValue({
        key: FRONTEND_STORAGE_KEYS.freelancer_billing_announcement_shown,
        value: true,
      });
    };

    dismissAnnoucement();
    const hasWithdrawalMethods = !!withdrawalMethods?.length;
    if (!isProfileCompleted || hasWithdrawalMethods) {
      return;
    }

    openFreelancerBillingAnnoucementModal();
  }, [
    shouldShowAnnouncement,
    setStorageValue,
    dispatch,
    isProfileCompleted,
    isFetchingWithdrawalMethods,
    withdrawalMethods,
  ]);

  return <FreelancerBillingAnnouncementModal />;
};
